/**
 * useExportToThrive — the shared selection + export-to-Thrive hook (subtask 07).
 *
 * One hook drives BOTH the Import-Detail write CTA — named per list type by
 * the page since cl.4 item 9, "Add Selected to search" / "…to Thrive" — and the
 * dedicated Export split-view.
 * It owns: the selection model, the per-contact W2 Skip/Update-anyway decisions,
 * the export mutation (202), and the poll-to-terminal query. On a terminal run
 * it surfaces a toast and lets the caller refetch the contacts/detail so the
 * affected rows gain the `exported` tag + the `exported_count` updates.
 *
 * Built against MSW from subtask 01 (no real backend) — `08` repoints the
 * client with no call-site changes. Cookie auth rides via the client's
 * `credentials: "include"`. English-only strings (workspace policy).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  AddToThriveRequest,
  ContactExportResult,
  ContactSelectionFilters,
  ExportIntent,
  ExportStatus,
  W2Decision as WireW2Decision,
} from "@nu-assist/contracts";

import { ImportsApiError, type ImportsClient } from "../../clients/imports.client";
import {
  EXPORT_COMPARE_EXPIRED_TOAST,
  EXPORT_NOT_COMPARED_TOAST,
  exportNotAddedClause,
  exportNothingWrittenLine,
} from "./exportConfirmationCopy";
import { type ExportRunSummary, exportRunSummary } from "./exportRunSummary";

/** A contact W2/W3 decision as carried on the export request.
 *
 *  RE-EXPORTED from the generated contract, not restated. It was a hand-kept
 *  copy of the same union, and when the wire gained a third arm (`add_as_new`,
 *  2026-09-08) this file was the one place that did not go red — it just
 *  narrowed the map on its way to the request, silently, which is the failure
 *  mode a duplicated vocabulary always has. */
export type W2Decision = WireW2Decision;

/** Terminal export job states — polling stops here. */
const TERMINAL_STATUSES: ReadonlySet<ExportStatus["status"]> = new Set(["succeeded", "failed"]);

export interface ExportOptions {
  /** `addToSearch` used to live here and was sent on every request as `false`
   *  while candidacies were created anyway — nothing downstream read it. The
   *  decision it pretended to make is now `intent` on the hook's arguments,
   *  which picks the ROUTE.
   *
   *  NOTE, out of scope but the same shape: `setOptions` is returned by this
   *  hook and called by nothing, so `updateCompany` / `updateWorkEmail` are
   *  also permanently `false` on the wire. */
  updateCompany: boolean;
  updateWorkEmail: boolean;
}

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  updateCompany: false,
  updateWorkEmail: false,
};

export interface UseExportToThriveArgs {
  client: ImportsClient;
  importId: string;
  /** Called when the run reaches a terminal state (caller refetches rows). */
  onTerminal?: (status: ExportStatus) => void;
  /** Toast sink — injected so the hook stays testable (defaults to no-op). */
  /** Sink for user-facing notices. `origin` tells the caller WHY it fired:
   *  `"terminal"` means a run this hook watched has finished, `"start"` means
   *  the POST itself failed and no run exists. The page suppresses its own
   *  completion notice on `"terminal"` only — a failed start is not a finished
   *  run, and treating it as one silenced the real completion toast.
   *
   *  `kind` has THREE values on purpose. `"info"` is the honest severity for a
   *  run that finished with an outcome we cannot read (see the `!countable`
   *  branch below); collapsing it into `"error"` toasted a destructive red
   *  banner for what may have been a clean success. */
  notify?: (
    message: string,
    kind: "success" | "error" | "info",
    origin?: "terminal" | "start"
  ) => void;
  /** Success wording, given the exported count. Omit for the generic default. */
  successMessage?: (exportedCount: number) => string;
  /** Wording for a run that finished with none of its rows in the payload, so
   *  no count can be claimed. A contract rather than a regex over
   *  `successMessage(0)`, which only worked while every caller ended its
   *  wording with the count. */
  incompleteMessage?: string;
  /** Poll interval (ms). Default 1500. */
  pollIntervalMs?: number;
  /**
   * Seed the selection (e.g. the Import-Detail table's checked rows carried into
   * the panel). Applied once on first render.
   */
  initialSelectedIds?: Iterable<string>;
  /**
   * Seed the per-contact W2 decisions (e.g. the choices the recruiter made in
   * the Compare modal, carried request-time into Export). Applied once on first
   * render.
   */
  initialW2Decisions?: Readonly<Record<string, W2Decision>>;
  /** WHICH write to perform. Defaults to the search path, which is what every
   *  list except a Company Map takes. The caller supplies it because only the
   *  page knows the import's `source`. */
  intent?: ExportIntent;
  /**
   * The whole-scope arm: a LANE FLAG plus the filters naming the scope.
   *
   * When set, `exportSelected` submits the filter-scoped write shape —
   * `select_all: true`, NO `contact_ids` — instead of materializing an id list
   * the client never fetched. `filters` is what makes the promise keepable
   * under an active filter: the server resolves `select_all` through the SAME
   * `ContactFilters` the contacts table sends on its read, so "all 230
   * matching your filters" writes those 230 and not the import's 1,300. Omit
   * it and `select_all` keeps its original meaning of "every contact in the
   * import".
   *
   * IT CARRIES NO COUNTS, and two stacked docblocks here went on describing a
   * `total` that seeded the progress denominator for a full round after it was
   * deleted (/pr-review iter 7, Agent C N5). The run total is the server's
   * `will_dispatch`, passed to `exportSelected` at the click — see there for
   * why that number had to stop coming from the page.
   */
  selectAllScope?: {
    filters?: ContactSelectionFilters | null;
  } | null;
}

export interface UseExportToThrive {
  /**
   * The exact body the run will POST — built once, shared with the preview.
   *
   * The panel owns the preview query (the compare gate lives there) and feeds
   * it THIS object, so the preview cannot describe a selection the run will not
   * make. Pinned by a test asserting both receive the same reference.
   */
  addRequestBody: AddToThriveRequest;
  selectedIds: ReadonlySet<string>;
  selectedCount: number;
  toggle: (contactId: string) => void;
  selectMany: (contactIds: string[]) => void;
  clearSelection: () => void;
  isSelected: (contactId: string) => boolean;
  /** Per-contact W2 decision (default `skip` when unset). */
  w2Decisions: Readonly<Record<string, W2Decision>>;
  setW2Decision: (contactId: string, decision: W2Decision) => void;
  options: ExportOptions;
  setOptions: (next: ExportOptions) => void;
  /** Fire the export of the current selection. */
  /**
   * Starts the run. Takes the SERVER's `will_dispatch` for the current
   * selection — see the implementation for why it is an argument and not a
   * prop (the preview is built on this hook's own `addRequestBody`).
   * Only the select-all lane reads it; the explicit lane counts its ids.
   */
  /**
   * Starts the run. Takes the SERVER's `will_dispatch` for the current
   * selection — a parameter, not a prop, because the preview query is built on
   * this hook's own `addRequestBody` and a prop would be a cycle.
   *
   * REQUIRED, with no default (/pr-review iter 7, Agent B B19). It was
   * `dispatchTotal?: number` defaulting to 0, which let two regressions
   * through: `onClick={xp.exportSelected}` hands the callback a `MouseEvent`,
   * and `() => xp.exportSelected()` hands it nothing. Making it required turns
   * BOTH into type errors at the call site. Only the select-all lane reads it;
   * the explicit lane counts its own ids.
   */
  exportSelected: (dispatchTotal: number) => void;
  isExporting: boolean;
  /** Latest poll result while/after a run (null before the first run). */
  exportStatus: ExportStatus | undefined;
  /**
   * Per-contact terminal results, keyed by contact id.
   *
   * ORPHANED BY THIS PR and knowingly left in place. Its only consumer was the
   * roster's row badges, which Trello #758 deleted; nothing outside this file's
   * own tests reads it now, and the contacts table behind the panel gets
   * per-contact outcomes from its own query rather than from here.
   *
   * Not deleted in this PR: removing an exported field of a shared hook is a
   * different change from a render swap, and this one arrived at review
   * iteration 10 on a branch whose whole claim is that the request path is
   * byte-identical. Flagged instead, so the next person to touch this hook can
   * remove it deliberately rather than discover it (/pr-review iter 10, Agent C).
   */
  resultsByContact: Readonly<Record<string, ContactExportResult>>;
  /**
   * The last TERMINAL run, aggregated — `null` before any run settles, and
   * `null` again for the duration of the next one. Also `null` when the status
   * payload carried none of the run's rows: the outcome is then unknown, and
   * the surface must say so rather than quote the import's cumulative totals.
   */
  runSummary: ExportRunSummary | null;
  /** Selected contacts the server left out of the current/last run because they
   *  need a new Compare to Thrive (Trello #823). 0 before any run and from the
   *  click until the 202 answers. */
  runHeldBackCount: number;
  /** `Date.now()` when this panel's run reached a terminal status, else 0. Lets
   *  a consumer ask whether what it is holding predates the run — see the state
   *  declaration for why a timestamp and not a boolean. */
  runSettledAt: number;
  /** Contacts selected when the current/last run was fired (progress denominator). */
  exportTotal: number;
  /** Progress numerator: contacts from THIS run that have SETTLED. Not "have a
   *  result" — `pending` rows have one too, and counting them made the bar
   *  report work as done before it was. */
  exportProcessed: number;
}

export function useExportToThrive(args: UseExportToThriveArgs): UseExportToThrive {
  const {
    client,
    importId,
    onTerminal,
    notify,
    successMessage,
    incompleteMessage,
    pollIntervalMs = 1500,
    initialSelectedIds,
    initialW2Decisions,
    intent = "add_to_search",
    selectAllScope = null,
  } = args;

  // Seed once via lazy initialisers so a parent re-render (new prop identities)
  // never clobbers in-panel selection/decision edits.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelectedIds ?? [])
  );
  const [w2Decisions, setW2Decisions] = useState<Record<string, W2Decision>>(() => ({
    ...(initialW2Decisions ?? {}),
  }));
  const [options, setOptions] = useState<ExportOptions>(DEFAULT_EXPORT_OPTIONS);
  const [polling, setPolling] = useState(false);
  const queryClient = useQueryClient();
  // Snapshot of the selection size when the run fired — the progress denominator
  // (ExportStatus carries no total; the FE owns it).
  const [exportTotal, setExportTotal] = useState(0);

  const toggle = useCallback((contactId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  }, []);

  const selectMany = useCallback((contactIds: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of contactIds) next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const isSelected = useCallback((contactId: string) => selectedIds.has(contactId), [selectedIds]);

  const setW2Decision = useCallback((contactId: string, decision: W2Decision) => {
    setW2Decisions((prev) => ({ ...prev, [contactId]: decision }));
  }, []);

  /**
   * The job id the 202 handed back for the run in flight.
   *
   * The server mints one per run (`uuid4()`), returns it in the 202 and keeps
   * it in `imports.export_run_status`. The poll used to answer with a constant
   * sentinel instead, which is why everything below had to guess whether a
   * terminal status was about THIS run or the previous one — see the terminal
   * effect. Now it does not have to guess.
   */
  const runJobIdRef = useRef<string | null>(null);

  // DECLARED ABOVE THE QUERY ON PURPOSE. `refetchInterval` closes over it and
  // TanStack constructs the observer during this same render, so a `const`
  // declared further down is still in its temporal dead zone when the option
  // is first evaluated — `ReferenceError` on mount, in every test that renders
  // the panel.
  /**
   * The Add request, built ONCE and handed to both the preview and the run.
   *
   * THE CHEAPEST ANTI-DRIFT GUARD THERE IS. The preview's whole promise is that
   * it describes the run that is about to happen; two literals built from the
   * same state in two places is how that promise stops being true, silently,
   * the first time one of them gains a field. A test asserts the mutation and
   * the preview receive the SAME OBJECT REFERENCE.
   *
   * Annotated, not inferred. Without the annotation TypeScript checks this by
   * structural assignability at the call sites, which does NOT apply
   * object-literal excess-property checking — so a stray field would compile
   * and ride the wire in silence (/pr-review iter 10, Agent A N8).
   * `AddToThriveRequest` satisfies both write routes and the preview:
   * `AddToSearchRequest`'s only extra field is optional, and the preview model
   * is `extra="ignore"`.
   *
   * Cross-page mode rides the FILTER-SCOPED shape: `select_all: true` and no
   * `contact_ids` (the two are mutually exclusive on the wire, and the whole
   * point is that no 1,300-id list ever exists client-side). The W2/W3 decision
   * map still travels — it is keyed by contact id and the server defaults any
   * unlisted contact to `skip`, so decisions made on the visible rows keep
   * their effect inside a select-all write.
   */
  const addRequestBody = useMemo<AddToThriveRequest>(
    () =>
      selectAllScope
        ? {
            select_all: true,
            // Only sent when the page actually has filters to honour. An
            // explicit `null` and an absent key mean the same thing on the
            // wire, but sending `filters: null` on every unfiltered run would
            // put a field in the body that says nothing — and `w2_decisions`
            // already makes this body's size the thing standing between a
            // select-all and the ALB's ~8 KB limit.
            ...(selectAllScope.filters ? { filters: selectAllScope.filters } : {}),
            update_company: options.updateCompany,
            update_work_email: options.updateWorkEmail,
            w2_decisions: w2Decisions,
          }
        : {
            contact_ids: Array.from(selectedIds),
            select_all: false,
            update_company: options.updateCompany,
            update_work_email: options.updateWorkEmail,
            // W2 decisions ride request-time; absent contacts default to skip.
            w2_decisions: w2Decisions,
          },
    [selectAllScope, selectedIds, options.updateCompany, options.updateWorkEmail, w2Decisions]
  );

  // Poll the export run until terminal. `enabled` flips on after the POST.
  const statusQuery = useQuery({
    queryKey: ["imports", importId, "export"],
    queryFn: () => client.getExport(importId),
    enabled: polling,
    refetchInterval: (query) => {
      const data = query.state.data;
      // TERMINAL **AND OURS**. This key is shared with the page, which keeps it
      // warm, so at click time the cache already holds a terminal status for
      // any list exported at any point in the past. Stopping on `terminal`
      // alone therefore stopped before the first poll of a run that had just
      // started — no poll ever ran, no status ever advanced, and the panel sat
      // on "running… 0 of 2 processed" forever with the rows already settled in
      // the database. Reproduced on app.local; the job id is what tells the two
      // runs apart.
      if (
        data &&
        TERMINAL_STATUSES.has(data.status) &&
        (runJobIdRef.current === null || data.job_id === runJobIdRef.current)
      ) {
        return false;
      }
      return pollIntervalMs;
    },
  });

  // Stop polling + toast + notify caller when the run goes terminal. Done in an
  // effect (not during render) so React Query's `enabled` flag flips cleanly and
  // the terminal side-effects fire exactly once per run.
  const status = statusQuery.data;
  // When the current `status` was FETCHED. The cache is shared with the page,
  // so identity alone cannot tell a status belonging to this run from one left
  // over by an earlier one; the fetch time can.
  const statusFetchedAt = statusQuery.dataUpdatedAt;
  const notifiedJobRef = useRef<string | null>(null);
  // When this hook's own run started, and which contacts it submitted.
  //
  // Both exist because `ExportStatus` describes the IMPORT, not a run. The
  // cache under `["imports", id, "export"]` is shared with the page and already
  // holds a terminal status for anything exported in the past, and
  // `exported_count` / `results` are cumulative across every run the import has
  // ever had. So "is this terminal status mine?" and "how many did I add?" both
  // need something the payload does not carry.
  const runStartedAtRef = useRef(0);
  const runContactIdsRef = useRef<Set<string>>(new Set());
  // Whether the current/last run was a filter-scoped select-all (sub-task 05).
  // A ref like its two siblings: it describes the RUN, not the render, and the
  // terminal effect + progress derivation must read the value the run fired
  // with even if the caller's props have since changed.
  const runIsSelectAllRef = useRef(false);
  /**
   * THE SELECT-ALL RUN'S SCOPE, observed rather than declared.
   *
   * A `select_all` sends no ids, so the client cannot name its own rows — and
   * `exportRunSummary`'s select-all arm therefore counted the WHOLE cumulative
   * payload. That was sound while `select_all` meant every row of the import.
   * `exportRunSummary.ts` says so, and names the day it stops being true:
   * *"the day a filtered select-all ships, 'every row in the payload belongs to
   * this run' becomes false and this arm silently over-counts"*. This PR shipped
   * that day twice over — filters on the `select_all` shape, and the
   * `only_resolvable` narrowing — so a run that dispatched 2 rows on a list with
   * 12 prior exports reported **"Added 14 of 14"** (/pr-review iter 4, Agent A
   * N3; latent-flagged by the same reviewer in iter 1).
   *
   * The signal we do have: **a row whose status CHANGED since the pre-run
   * snapshot belongs to this run.** Accumulated as a union across polls,
   * because a single poll sees only part of the movement.
   *
   * NOT "seen `pending` after the run started", which is what this block said
   * for three consecutive review iterations after the code stopped doing it
   * (/pr-review iter 3 N3, iter 4 N4, iter 5 A — byte-identical each time,
   * because every round fixed the docstring 20 lines below instead). `pending`
   * survives only as the FALLBACK arm, for the one case with no snapshot to
   * compare against; the accumulator itself reads
   * `preRunStatusRef`, and that ref's own docblock lists the
   * seen-pending rule as a REJECTED alternative.
   *
   * THE RESIDUAL LIMIT IS GONE ON THE CHANGED-SINCE ARM, AND ONLY THERE — the
   * clause here used to say it was gone outright (/pr-review iter 7, Agent C).
   * "A row that settles before the first post-run poll is never observed
   * `pending` and is missed" was true of the old rule, and is false of the
   * changed-since comparison: such a row moved, so it is in the set, and it is
   * not pending. That is exactly the case that rule was adopted to cover.
   *
   * But the ternary below has a SECOND arm. With no pre-run snapshot
   * (`hadSnapshot === false`) it falls back to `export_status === "pending"`,
   * and on that arm the old limit still holds exactly as stated. The fallback
   * runs on the first Add of an import that has never been exported, which is
   * not a rare path. Saying "gone" of both arms described the one that was
   * fixed and quietly generalised to the one that was not.
   *
   * What remains true: a per-row run signal on the wire would be sturdier than
   * any snapshot diff (`ContactExportResult` carries no `job_id`), and that is
   * a migration this card is not taking.
   */
  const runPendingIdsRef = useRef<Set<string>>(new Set());
  /**
   * What every row's `export_status` was the instant this run was fired.
   *
   * The scope signal that actually works. A `select_all` sends no ids, so the
   * client cannot name its own rows, and the two cheaper substitutes each
   * failed on a case:
   *
   *   * "every row in the payload" over-counts — `only_resolvable` means an
   *     already-exported contact is never dispatched, yet it sits in the
   *     cumulative payload forever ("Added 14 of 14" for a 2-row run).
   *   * "every row seen `pending` since the run started" misses a row that
   *     settles before the first post-run poll, and still picks up a corpse
   *     left `pending` by a run that died mid-flight.
   *
   * A ROW BELONGS TO THIS RUN WHEN ITS STATUS DIFFERS FROM THIS SNAPSHOT.
   * Prior exports were `exported` before and still are, so they never qualify;
   * a corpse was `pending` before and still is, so it does not either; a row
   * this run touched went absent -> `pending` -> terminal and differs at every
   * step, including the one where it skipped `pending` entirely.
   *
   * "…OR WHEN IT IS PENDING NOW" WAS PART OF THIS RULE AND IS NOT ANY MORE.
   * OR-ing it on unconditionally readmitted the corpse, and an earlier version
   * of this block asserted the corpse "never qualifies" while the code let it
   * in — with a third comment 300 lines away saying the opposite. The `pending`
   * test now applies ONLY when no pre-run payload existed at all, which is the
   * one case with nothing to compare against (/pr-review iter 3, Agent A N3).
   */
  const preRunStatusRef = useRef<Map<string, string | null | undefined>>(new Map());
  /** The latest payload, for reading at dispatch time without re-creating the
   *  callback on every poll. */
  const latestStatusRef = useRef<ExportStatus | undefined>(undefined);
  /**
   * Was there a payload at all when this run fired — regardless of how many
   * rows it held?
   *
   * NOT the same question as "is the snapshot non-empty", and conflating the
   * two broke the most common path in the product. `get_export` returns only
   * contacts that have entered the export lifecycle
   * (`service_export.py`: `export_status is not None`), so a never-exported
   * import answers `results: []` — a real payload, legitimately empty. Reading
   * emptiness as "nothing was fetched" sent every FIRST Add down the fallback
   * arm, where scope is "is pending"; a run that settles before the first poll
   * then produced no scope at all, the summary rendered "outcome unknown", and
   * `awaitAddResult` throws on that (/pr-review iter 2, Agent C N1).
   *
   * With the payload present and empty, "absent from the snapshot" is exactly
   * right: every row that appears afterwards is this run's.
   */
  const hadPreRunPayloadRef = useRef(false);
  /**
   * What the last terminal run did, aggregated — the panel's post-run surface
   * since Trello #758 replaced the per-contact status list.
   *
   * Held as STATE computed once in the terminal effect, not derived on every
   * render from the status query. The query's cache is shared with the page and
   * keeps reporting cumulatively forever; freezing the summary at the moment
   * the effect already holds this run's scoped rows is what stops a later
   * unrelated export from rewriting a result the recruiter is reading.
   */
  const [runSummary, setRunSummary] = useState<ExportRunSummary | null>(null);
  // What the 202 says this run left out. Read from the 202, not the preview: the
  // server re-checks at send time, so a row can age out between the two.
  const [runHeldBackCount, setRunHeldBackCount] = useState(0);
  /**
   * When this panel's run reached a terminal status, as `Date.now()`, or 0 if
   * no run has settled since the panel opened.
   *
   * It exists so a consumer can ask ONE question the panel kept answering with
   * proxies: **is what I am holding older than the run?** The Add preview is a
   * cached query whose key does not change across a select-all run, so after
   * the run TanStack keeps serving the pre-run tally while it refetches, and
   * keeps serving it FOREVER if that refetch fails (`retry: false` plus
   * retain-on-error). Three rounds of guards approximated this with
   * `exportTotal === 0`, which is set at the click and answers a different
   * question (/pr-review iter 8, Agents A, B and C — the same defect found
   * independently three ways).
   *
   * Reset to 0 when a new run starts, so the next run's tally is judged against
   * the next run.
   */
  const [runSettledAt, setRunSettledAt] = useState(0);
  // Keep the newest payload where `exportSelected` can read it without taking
  // `status` as a dependency — the callback must not be re-created on every
  // poll, and the snapshot it takes has to be the payload as of the click.
  useEffect(() => {
    latestStatusRef.current = status;
  }, [status]);

  // Accumulate the select-all scope on EVERY post-run-start poll — this must
  // run on non-terminal polls too, which is exactly when the rows are still
  // visible as `pending`, and so it cannot live inside the terminal effect
  // below (whose first line returns on a non-terminal status).
  //
  // DECLARED ABOVE THE NEXT EFFECT'S `biome-ignore`, not between it and its
  // effect. Inserted in the gap it detached the suppression from the terminal
  // effect and attached it to this one, where it is unused — seven lint errors
  // and a warning, and the same misplacement class as the `@model_validator`
  // that landed in the wrong class earlier in this PR.
  useEffect(() => {
    if (!status || !runIsSelectAllRef.current) return;
    if (statusFetchedAt <= runStartedAtRef.current) return;
    // AN EMPTY SNAPSHOT IS NOT AN EMPTY PAYLOAD. If no status had been fetched
    // when the run fired there is nothing to compare against, and treating
    // "absent from the snapshot" as "new" would put every cumulative row in
    // scope — the "Added 14 of 14" bug, rebuilt. In that case fall back to the
    // only signal left, the rows this run stamped `pending`.
    // The PAYLOAD's presence, not the snapshot's size — an empty payload is a
    // fact about the import, not a missing fetch. See `hadPreRunPayloadRef`.
    const hadSnapshot = hadPreRunPayloadRef.current;
    for (const r of status.results ?? []) {
      // TWO RULES, AND ONLY ONE APPLIES AT A TIME. The `pending` test used to
      // be OR-ed onto the comparison unconditionally, which quietly undid the
      // thing the snapshot was added for: a row left `pending` by a run that
      // died mid-flight is pending on every later poll, so it was readmitted
      // to every later run's scope. The docstring above said it "never
      // qualifies" and the progress-bar comment below said it joins the set —
      // both describing the same clause, disagreeing, and neither falsifiable:
      // deleting the clause left all 2,806 web tests green (/pr-review iter 2,
      // Agent B F0).
      //
      // With a snapshot in hand, "changed since the click" is strictly better
      // and needs no help: this run's rows moved (absent/`null` -> `pending` ->
      // terminal, or straight to terminal on a fast run), and the corpse did
      // not move at all. `pending` is the fallback for the one case where
      // there is nothing to compare against.
      const inScope = hadSnapshot
        ? !preRunStatusRef.current.has(r.contact_id) ||
          preRunStatusRef.current.get(r.contact_id) !== r.export_status
        : r.export_status === "pending";
      if (inScope) runPendingIdsRef.current.add(r.contact_id);
    }
  }, [status, statusFetchedAt]);

  // `notify`/`onTerminal` are caller-stable callbacks; depending on them would
  // re-fire the terminal side-effects on identity changes. The once-guard
  // (notifiedJobRef) already ensures exactly-once per run.
  // biome-ignore lint/correctness/useExhaustiveDependencies: notify/onTerminal are stable sinks; once-guard prevents re-fire
  useEffect(() => {
    if (!polling || !status) return;
    if (!TERMINAL_STATUSES.has(status.status)) return;
    // Fetched BEFORE this run started ⇒ it describes a previous one.
    //
    // `["imports", id, "export"]` is shared with the page, which keeps it
    // enabled, so at click time the cache already holds a terminal `succeeded`
    // for any list exported at any point in the past — `get_export` reports
    // that forever. Reading it announced the PREVIOUS run's result the instant
    // Add was clicked, cleared the selection, and left `polling` false, so the
    // run actually starting never spoke. The bug predates cl.4; nothing passed
    // `notify` before, which is the only reason it went unnoticed.
    //
    // FETCH TIME rather than "have I seen it running": an earlier revision used
    // the latter and hung the CTA on "Working…" for a run that finished before
    // its first poll — it never spoke and so never cleared `polling`.
    //
    // IS THIS STATUS ABOUT OUR RUN? Ask the job id, which since 2026-09-17 is
    // the real per-run `uuid4()` the 202 handed back rather than a server-side
    // constant. The comment here used to say the opposite and the code below
    // compared FETCH TIMES instead — a proxy that answers "is this newer than
    // my click?", which is not the same question and breaks in both directions:
    // a cached terminal status that never refetches keeps the spinner up
    // forever (the bug this replaces), and a poll that lands late enough would
    // have announced the previous run's numbers as this one's.
    if (runJobIdRef.current !== null && status.job_id !== runJobIdRef.current) return;
    // Guard against double-firing across re-renders of the same terminal run.
    if (notifiedJobRef.current === status.job_id) return;
    notifiedJobRef.current = status.job_id;
    setPolling(false);
    // Scope the numbers to the contacts THIS run submitted. `exported_count` and
    // `results` are cumulative, so reading them whole announced totals: adding 3
    // to a list with 12 already exported said 15. And `_derive_run_status` calls
    // a run `succeeded` whenever ANY row on the import is exported, so a run
    // whose contacts all failed took the success branch and reported the older
    // successes with no mention of the failures.
    // ONE definition of "this run's rows", shared with the panel's result
    // heading — see `exportRunSummary`, which carries the reasoning for the
    // select-all arm and for why an empty scope yields no number at all rather
    // than falling back to the cumulative payload.
    const summary = exportRunSummary(status.results ?? [], {
      contactIds: runIsSelectAllRef.current ? runPendingIdsRef.current : runContactIdsRef.current,
    });
    setRunSummary(summary);
    setRunSettledAt(Date.now());
    const exportedNow = summary?.exported ?? 0;
    const failedNow = summary?.failed ?? 0;
    // Rows this run SENT and did not WRITE. The eighth surface where one number
    // answered two questions, and it was inside the fix for the seventh: the
    // page's toast learned to name a zero-write run, and this one — the toast a
    // recruiter actually sees, because `hookSpokeRef` suppresses the page's
    // whenever the panel is open — never read this number at all. Three of the
    // four cells still announced "Added 0 contacts" as a success
    // (/pr-review iter 3, Agent A N1).
    const skippedNow = summary?.skipped ?? 0;
    const countable = summary !== null;
    if (!countable) {
      // Terminal, ours, but the payload carries none of our rows. Two things
      // follow, and both were wrong in the first version of this branch.
      //
      // No NUMBER: there is nothing to count, and a regex over
      // `successMessage(0)` only looked like one — it depended on every caller
      // ending its wording with the count, and dropped the punctuation when
      // they did. `incompleteMessage` is a contract instead of a pattern.
      //
      // And not `"success"`: we cannot see whether these contacts landed, so a
      // green tick would assert an outcome we do not have. An all-failed run
      // whose rows are missing would have got one.
      //
      // Not `"error"` either, which is what the first fix reached for. The
      // outcome here is UNKNOWN, not bad — the run may have fully succeeded and
      // the payload simply not carry its rows yet — so a destructive red banner
      // asserts a failure just as unfoundedly as the green tick asserted a
      // success. `"info"` is the only severity that matches what we know
      // (/pr-review iter 1, Agent A 2(c) / Agent C M2).
      notify?.(
        incompleteMessage ?? "The export finished. Open the contacts table to see the results.",
        "info",
        "terminal"
      );
    } else if (failedNow === 0 && exportedNow === 0 && skippedNow > 0) {
      // NOT GATED ON `status.status`, and that omission is the point.
      //
      // `_derive_run_status` computes that field over the import's CUMULATIVE
      // rows, while every number in these arms is scoped to THIS run. One older
      // `failed` row on the import is enough to make it report something other
      // than `succeeded` forever, and both correct arms then became
      // unreachable: a clean zero-write run fell through to the failure arm and
      // told the recruiter to "retry" a row that needs a DECISION, with a
      // "1 other" whose antecedent was never printed.
      //
      // One selector reading a cumulative fact while its own numbers are
      // per-run is this card's defect, on the ninth surface and inside the fix
      // for the eighth (/pr-review iter 4, Agent C N1). The page never had it:
      // it selects on `failed` and `exported` alone. So does this now.
      // NOTHING WRITTEN, AND NOT A FAILURE. The rows went, the worker resolved
      // each open conflict to a skip, and they come back flagged To review —
      // the state this card fought to keep runnable. "Added 0 contacts" as a
      // success is true and useless; `info` naming the outcome is what the
      // recruiter can act on. NOT the word `skipped`, which the table stopped
      // displaying after #748.
      notify?.(exportNothingWrittenLine(skippedNow), "info", "terminal");
    } else if (failedNow === 0) {
      // Wording is the CALLER's, because it is per-list-type: a Company Map's
      // contacts go to Thrive without joining the search, and this notice is
      // the last surface a recruiter sees for the action, so it must not keep
      // the retired "Export" verb the buttons no longer use (Trello #711 cl.4,
      // §13 of the 2026-08-18 call). Default preserves the original string, so
      // a caller that does not care is unaffected.
      // A MIXED RUN NAMES BOTH HALVES. Some rows were written and some came
      // back flagged To review, and announcing only the first is the same
      // omission as announcing "Added 0" — smaller, and on the more common
      // shape. The success stands, because contacts really did land.
      const alsoNotAdded = exportNotAddedClause(skippedNow);
      notify?.(
        (successMessage
          ? successMessage(exportedNow)
          : `Export complete. ${exportedNow} contact${
              exportedNow === 1 ? "" : "s"
            } exported to Thrive.`) + alsoNotAdded,
        "success",
        "terminal"
      );
    } else {
      // Both halves, and the caller's verb: a partly-failed run is `succeeded`
      // server-side, so without this the recruiter heard only a success.
      const added = exportedNow > 0 && successMessage ? `${successMessage(exportedNow)} ` : "";
      const failedAlsoNotAdded = exportNotAddedClause(skippedNow);
      notify?.(
        (failedNow > 0
          ? `${added}${failedNow} contact${failedNow === 1 ? "" : "s"} failed. See the per-contact details and retry.`
          : "Nothing was added. See the per-contact details and retry.") +
          // The sixth cell. A run can fail some rows AND leave others flagged;
          // naming only the failures is the same omission one cell over, which
          // is how this defect reached eight instances.
          failedAlsoNotAdded,
        "error",
        "terminal"
      );
    }
    // Reconcile the read model so gating is correct the instant a run settles:
    // refresh BOTH contact-list surfaces (the panel's own list + the
    // import-detail table, which use different query keys) and the detail
    // header, then clear the selection. Without this the just-exported rows
    // stay checked and re-selectable (stale `export_status` + a live selection),
    // which let the same contacts be exported again.
    queryClient.invalidateQueries({ queryKey: ["imports", importId, "contacts"] });
    // The run changed what a NEXT run would do — rows moved to `exported` or
    // `skipped`, so the preview's tally is stale the moment this one lands.
    //
    // CANCEL BEFORE INVALIDATING, or the re-read never happens (/pr-review iter
    // 9, Agent C N8). A preview fetch can still be IN FLIGHT when the run
    // settles — a recruiter who reopens the panel gets the cached tally served
    // immediately while `refetchOnMount: "always"` starts a fresh read, and can
    // click before it lands. In that state the invalidate runs against a
    // disabled observer, so it marks the query stale WITHOUT refetching; the
    // re-enable then dedupes against the in-flight pre-run fetch; and that
    // fetch commits its pre-run data and clears the invalidation on the way
    // past. Measured: one `previewAdd` call across the whole sequence, and the
    // stale tally lands with a POST-run `dataUpdatedAt`, which is exactly the
    // comparison `tallyPredatesRun` makes — so the guard reads fresh and the
    // CTA goes live.
    //
    // Cancelling first discards that in-flight result instead of letting it
    // commit, so the invalidate has something to refetch.
    queryClient.cancelQueries({ queryKey: ["imports", importId, "add-preview"] });
    queryClient.invalidateQueries({ queryKey: ["imports", importId, "add-preview"] });
    queryClient.invalidateQueries({ queryKey: ["import-contacts", importId] });
    queryClient.invalidateQueries({ queryKey: ["import-detail", importId] });
    setSelectedIds(new Set());
    onTerminal?.(status);
  }, [polling, status]);

  const exportMutation = useMutation({
    mutationFn: () => {
      // The route, not a flag. Which write this is belongs to the CALLER —
      // the page knows the list's `source`, the hook does not.
      return intent === "add_to_thrive"
        ? client.addToThrive(importId, addRequestBody)
        : client.addToSearch(importId, addRequestBody);
    },
    onSuccess: (accepted) => {
      // 202 accepted — begin polling to terminal. Reset the once-guard so a new
      // run's terminal toast fires even if the previous run already toasted.
      //
      // ORDER IS LOAD-BEARING, and it is the kind of order a tidy-up would
      // reshuffle without noticing (/pr-review iter 1, Agent A 3(b)). The three
      // ref writes MUST all precede `setPolling(true)`: the terminal effect
      // keys on `polling`, and it compares the status's fetch time against
      // `runStartedAtRef` and scopes its counts through `runContactIdsRef`. Set
      // `polling` first and the effect can run its guards against the PREVIOUS
      // run's timestamp and id set. Refs are written synchronously and
      // `setPolling` only schedules, so this ordering holds under React 19's
      // batching — but only while it is written this way round.
      //
      // `runContactIdsRef` is seeded here AND in `exportSelected`, deliberately
      // (Agent C M5). The two seeds read the same `selectedIds` snapshot; this
      // one also drops the rows the server held back (below), and it exists so
      // a retry after a rejected POST cannot start polling against a scope the
      // earlier attempt left behind. Deleting the `exportSelected` seed as "the duplicate"
      // revives "Exporting… 5 of 2" — see the comment there before touching
      // either.
      notifiedJobRef.current = null;
      runStartedAtRef.current = Date.now();
      // THIS run's id, straight off the 202. Everything that has to tell our
      // terminal status from the previous one keys on it.
      runJobIdRef.current = accepted?.job_id ?? null;
      // The previous run's result must not stand over a run in flight.
      setRunSummary(null);
      // Select-all runs have no id list to seed — their scope flag was set in
      // `exportSelected`, and re-seeding from `selectedIds` here would hand a
      // stray in-panel selection to the run-scoping of a run that ignored it.
      // THE ROWS SENT, NOT THE ROWS SELECTED (/pr-review iter 6, Agent C I1).
      // Rows needing a new Compare are left out by the server and never get a
      // status, so a scope seeded from the selection left the bar short of its
      // total forever and counted those rows' OLD outcomes as this run's.
      const heldBack = new Set((accepted?.held_back_contact_ids ?? []).map(String));
      // Set with `setRunSummary(null)` above, never at the click: a rejected
      // POST keeps the previous result card, and that card keeps its own line
      // (/pr-review iter 7, Agent C m2).
      setRunHeldBackCount(accepted?.held_back_count ?? 0);
      if (!runIsSelectAllRef.current) {
        const sent = new Set([...selectedIds].filter((id) => !heldBack.has(id)));
        runContactIdsRef.current = sent;
        setExportTotal(sent.size);
      } else if (typeof accepted?.dispatched_count === "number") {
        // Select-all: the click's total was the preview's `will_dispatch`, and
        // the server decides again at send time. A row can age out, or a
        // Compare elsewhere can make one writable, in between; the 202 counts
        // what was actually sent (/pr-review iter 7, Agent A).
        setExportTotal(accepted.dispatched_count);
      }
      setPolling(true);
      // KICK THE FIRST POLL EXPLICITLY. Flipping `enabled` is not enough in the
      // app: this key already has an observer — the page's — and on the browser
      // the transition produced NO request at all, verified on app.local with
      // the network log (one GET at page load, none after the 202). Whatever
      // the exact rule, resting a run's only feedback loop on a library's
      // enable-transition semantics is resting it on something this codebase
      // does not test and cannot see.
      //
      // Safe only because the poll now returns the REAL per-run job id: an
      // invalidation that lands before the worker has moved re-reads the
      // PREVIOUS run's terminal status, and both the terminal effect and
      // `refetchInterval` now reject it as not-ours and keep going. Before that
      // server fix, this same line would have announced the previous run's
      // numbers as this one's.
      queryClient.invalidateQueries({ queryKey: ["imports", importId, "export"] });
    },
    onError: (err) => {
      // A START THE SERVER REFUSED IS NOT A RUN (/pr-review iter 8, Agent C
      // N7). `exportTotal` is set optimistically at the click so the
      // denominator is right from the first frame, and nothing put it back
      // when the POST was rejected. `exportTotal` is one of the terms gating
      // the confirmation sentence and the not-added note, so a refused start
      // deleted both PERMANENTLY — while the toast asked for a retry and the CTA
      // offered one, with no progress bar and no result card to have replaced
      // them. The panel was left saying nothing at all about a selection it
      // was still willing to send.
      setExportTotal(0);
      // Re-read the tally. A refusal often means it changed under the panel,
      // most of all the fresh-compare 409 (Trello #823): a panel left open past
      // the window still shows a live CTA, and this re-read is what turns it
      // into the held line.
      queryClient.invalidateQueries({ queryKey: ["imports", importId, "add-preview"] });
      // KNOWN DEBT, not fixed on this card (`/pr-review` iters 6-8, Agent C).
      // The API documents six distinct 409s and the service writes actionable
      // detail for each — "Link it to a search first, or supply
      // `search_id_thrive`" is one. This collapses all of them into "try
      // again", and TWO of the four ("wrong endpoint for this list type", "no
      // linked search") can never succeed on a retry, so the copy is not merely
      // vague, it is wrong advice. Surfacing `detail` is a UX change wider than
      // this card; it needs its own. Recorded here rather than only in review,
      // because a deferral nobody wrote down is just a thing nobody did.
      // The two 409s that carry a machine reason are named (Trello #823):
      // "try again" is wrong advice for both, since a retry is refused the
      // same way until a compare runs. The other 409s stay on the debt
      // recorded above.
      if (err instanceof ImportsApiError && err.status === 409) {
        const reason = err.detailData?.reason;
        if (reason === "not_compared") {
          notify?.(EXPORT_NOT_COMPARED_TOAST, "error", "start");
          return;
        }
        if (reason === "compare_too_old") {
          notify?.(EXPORT_COMPARE_EXPIRED_TOAST, "error", "start");
          return;
        }
      }
      notify?.("Could not start the export. Please try again.", "error", "start");
    },
  });

  /**
   * `dispatchTotal` is the SERVER's `will_dispatch` for this selection, and it
   * is a parameter rather than a prop because the preview query is built on
   * `addRequestBody`, which this hook returns — passing it in would be a cycle.
   *
   * It replaces `selectAllScope.total`, the page's client-side `add_resolvable`
   * count (/pr-review iter 6, Agent C N1, and step 14 of the approved plan).
   * That left the two questions answered by two different numbers: the CTA was
   * gated on the server's `will_dispatch` while the RUN was gated on the
   * page's, so a selection the button offered could produce no POST at all, and
   * the progress denominator came from the number that had not run the write
   * policy. One value, one question, one source.
   */
  const exportSelected = useCallback(
    (dispatchTotal: number) => {
      if (selectAllScope) {
        // Filter-scoped run: the denominator is the server's count for the scope
        // (no client-side ids exist to count). The empty-scope guard mirrors the
        // empty-selection one below — a claim of "all 0" starts nothing.
        if (dispatchTotal === 0) return;
        setExportTotal(dispatchTotal);
        setRunSettledAt(0);
        runIsSelectAllRef.current = true;
        runContactIdsRef.current = new Set();
        runPendingIdsRef.current = new Set();
        hadPreRunPayloadRef.current = latestStatusRef.current !== undefined;
        preRunStatusRef.current = new Map(
          (latestStatusRef.current?.results ?? []).map((r) => [r.contact_id, r.export_status])
        );
        exportMutation.mutate();
        return;
      }
      runIsSelectAllRef.current = false;
      if (selectedIds.size === 0) return;
      setExportTotal(selectedIds.size);
      setRunSettledAt(0);
      // Seed the run scope HERE, not only in `onSuccess`: `exportTotal` is set on
      // submit, so between the click and the 202 the denominator was the new run
      // while the numerator still counted the previous one — "Exporting… 5 of 2".
      // `onSuccess` re-seeds from the same selection minus the rows the server
      // held back for a new Compare, and corrects the total to match.
      // INTENTIONAL DOUBLE-SEED, not a leftover (Agent C M5): this one makes the
      // denominator correct from the click, `onSuccess`'s makes it correct from
      // the 202. Removing either brings back a wrong progress pair.
      runContactIdsRef.current = new Set(selectedIds);
      exportMutation.mutate();
    },
    [selectedIds, exportMutation, selectAllScope]
  );

  const resultsByContact = useMemo(() => {
    const map: Record<string, ContactExportResult> = {};
    for (const r of status?.results ?? []) map[r.contact_id] = r;
    return map;
  }, [status]);

  return {
    selectedIds,
    // selectedCount MUST equal selectedIds.size at all times — ExportToThrivePanel's
    // sortedRows useMemo depends on it as a stable scalar proxy for selectedIds so
    // that biome-ignore on useExhaustiveDependencies is safe. Never derive selectedCount
    // from anything other than selectedIds.size.
    selectedCount: selectedIds.size,
    toggle,
    selectMany,
    clearSelection,
    isSelected,
    w2Decisions,
    setW2Decision,
    options,
    setOptions,
    exportSelected,
    /**
     * The exact object the run will POST. Exposed so the PANEL can hand it to
     * the preview: the preview must describe this run, and the only way to be
     * sure is for both to hold the same reference. The panel owns the query
     * rather than the hook because the compare gate (`blocked`) lives there.
     */
    addRequestBody,
    isExporting: exportMutation.isPending || polling,
    exportStatus: status,
    resultsByContact,
    runSummary,
    runHeldBackCount,
    runSettledAt,
    exportTotal,
    // Scoped to the ids THIS run submitted, and counting only rows that have
    // actually settled. `Object.keys(resultsByContact).length` did neither: it
    // counted every contact the import has ever exported (the payload is
    // cumulative) AND counted `pending` rows as processed — so a one-contact
    // run on a list with 12 prior exports rendered "Exporting… 13 of 1".
    exportProcessed: runIsSelectAllRef.current
      ? // Select-all run. COUNTED DOWN FROM THE PENDING ROWS, not up from the
        // settled ones, and the difference is a regression this PR introduced.
        //
        // The old rule was "settled rows in the payload", resting on "the POST
        // stamps EVERY contact in scope `pending`". That premise held while
        // `select_all` meant every row of the import. It does not any more:
        // `_resolve_selection` now narrows to `_export_resolvable_condition`,
        // so an already-exported row is never stamped `pending` and counts as
        // settled from the first poll — while the denominator (`add_resolvable`)
        // excludes it. On an import with 12 exported and 2 to add, that renders
        // **"Exporting… 13 of 2"** (/pr-review iter 4, Agent A N2).
        //
        // `total - pending`, WITH PENDING TAKEN FROM THIS RUN'S SCOPE.
        //
        // The subtrahend used to be every pending row in the payload, which is
        // cumulative. `pending` does not clear itself: a run killed between its
        // claim and its first terminal write leaves rows in it, and every later
        // run then subtracted that corpse from its own progress — a two-contact
        // run pinned at "0 of 2" start to finish. The mitigation offered for it
        // (the claim guard refuses a second run) only holds until the claim's
        // TTL expires, and a FILTERED select-all with a corpse outside the
        // filter is not covered by it at all — a shape this PR introduced, so
        // the mitigation predates the case (/pr-review iter 2, Agent A L3).
        //
        // Intersecting with `runPendingIdsRef` was tried a round earlier and
        // did nothing, because that set then admitted anything `pending` — the
        // corpse included. It works now, and only now, because membership is
        // "changed since the click": this run's in-flight rows are in it
        // (absent -> `pending` is a change), a row that settled before the first
        // poll is in it and is not pending, and the corpse never moved so it is
        // not in it at all. All three cases fall out of one expression.
        statusFetchedAt > runStartedAtRef.current
        ? Math.max(
            0,
            exportTotal -
              (status?.results ?? []).filter(
                (r) => r.export_status === "pending" && runPendingIdsRef.current.has(r.contact_id)
              ).length
          )
        : 0
      : (status?.results ?? []).filter(
          (r) => runContactIdsRef.current.has(r.contact_id) && r.export_status !== "pending"
        ).length,
  };
}
